import httpService from "./httpService";

// ============================================================
// User Types
// ============================================================

/**
 * User type for the /v2/users endpoint
 */
export interface User {
  id: string;
  email: string;
  name?: string | null;
  ibkrAccountId?: string | null;
  roles?: string[];
  scopes?: string[];
  emailVerified: boolean;
  createdAt: string;
  updatedAt: string;
  providerId?: string;
  provider?: {
    id: string;
    name: string;
  } | null;
}

/**
 * Parameters for finding users
 */
export interface FindUsersParams {
  roles?: ("ADMIN" | "INVESTOR" | "PROVIDER" | "ADVISOR")[];
  searchString?: string;
  searchColumn?: "id" | "name" | "email" | "providerName";
  orderByColumn?: string;
  orderByDirection?: "asc" | "desc";
  pageSize?: number;
  pageNumber?: number;
}

/**
 * Paginated response from /v2/users
 */
export interface PagedUsersResponse {
  currentPage: number;
  currentPageNumOfItems: number;
  totalPages: number;
  totalItems: number;
  items: User[];
}

/**
 * Response shape from GET /v2/admin/users/:id
 */
export interface AdminUserLookupResponse {
  user: User;
}

export interface AdminUserUpdate {
  name?: string | null;
  emailVerified?: boolean;
  roles?: string[];
  scopes?: string[];
}

/**
 * Optional per-user configuration blob returned by the bulk lookup endpoint
 * (the nullable `data` JSON column on `user_configuration`, added in
 * PWA-2435). This is the raw admin-editable JSON document passed straight
 * through by the backend (`UserAdminController.lookupUsers`), so admin-set
 * flags like `tradingenabled` are usually the string "true"/"false" but are
 * not strictly typed -- the backend's own eligibility check
 * (`investorStrategyService.getTradingEnabledUsers`) stringifies before
 * comparing, so callers should do the same rather than assume a fixed type.
 * `userSettings.user_tradingenabled` is user-managed and an actual boolean.
 */
export interface UserLookupData {
  isbeta?: string;
  iscustomer?: string;
  launch_date?: string;
  isretirement?: string;
  payment_rate?: number;
  userSettings?: {
    user_tradingenabled?: boolean;
  };
  margin_enabled?: string;
  tradingenabled?: string | boolean;
  paying_customer?: string;
  datepaying_since?: string;
  margin_allowed_amt?: number;
}

/**
 * Per-user summary returned by the bulk lookup endpoint.
 */
export interface UserLookupSummary {
  name: string | null;
  email: string;
  data: UserLookupData | null;
  ibkrAccountId?: string | null;
}

/**
 * Response shape from POST /v3/admin/users/lookup: a userId -> summary
 * mapping. UserIds with no matching user are simply absent from the object.
 */
export type BulkUserLookupResponse = Record<string, UserLookupSummary>;

// ============================================================
// Bulk Investor Roster Sync Types
// ============================================================

/**
 * Provider subscription for bulk user creation
 */
export interface BulkUserProviderSubscription {
  providerId: number;
  baskets: number[];      // [] = all, [-1] = none/public-only, [ids] = specific
  portfolios: number[];   // [] = all, [-1] = none/public-only, [ids] = specific
}

/**
 * Single user entry for bulk update request
 */
export interface BulkUserEntry {
  email: string;
  name?: string;
  providerSubscriptions: BulkUserProviderSubscription[];
}

/**
 * Request body for POST /v3/investors/bulk-update
 */
export interface BulkUpdateInvestorsRequest {
  users: BulkUserEntry[];
}

/**
 * Response from POST /v3/investors/bulk-update
 */
export interface BulkUpdateInvestorsResponse {
  message: string;
  usersProcessed: number;
}

/**
 * Service for User API operations
 * Uses the /v2/users endpoint
 */
class UsersService {
  private readonly baseUrl = "/v2/users";

  /**
   * Find users with filtering, pagination, and ordering
   */
  async findUsers(params: FindUsersParams = {}): Promise<PagedUsersResponse> {
    const queryParams = new URLSearchParams();

    if (params.roles && params.roles.length > 0) {
      queryParams.set("roles", params.roles.join(","));
    }
    if (params.searchString) {
      queryParams.set("searchString", params.searchString);
    }
    if (params.searchColumn) {
      queryParams.set("searchColumn", params.searchColumn);
    }
    if (params.orderByColumn) {
      queryParams.set("orderByColumn", params.orderByColumn);
    }
    if (params.orderByDirection) {
      queryParams.set("orderByDirection", params.orderByDirection);
    }
    if (params.pageSize !== undefined) {
      queryParams.set("pageSize", String(params.pageSize));
    }
    if (params.pageNumber !== undefined) {
      queryParams.set("pageNumber", String(params.pageNumber));
    }

    const queryString = queryParams.toString();
    const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;

    const response = await httpService.get<PagedUsersResponse>(url);
    return response.data;
  }

  /**
   * Look up a single user by id (admin-only).
   * GET /v2/admin/users/:id
   */
  async getUserById(id: string): Promise<User> {
    const response = await httpService.get<AdminUserLookupResponse>(
      `/v2/admin/users/${id}`,
      { requiresAuth: true, requestScope: "actor" }
    );
    return response.data.user;
  }

  /** Explicit roles/scopes replace saved assignments together; omitted fields preserve them. */
  async updateUser(id: string, update: AdminUserUpdate): Promise<User> {
    const response = await httpService.put<AdminUserLookupResponse>(
      `/v2/admin/users/${id}`, update, { requiresAuth: true, requestScope: "actor" }
    );
    return response.data.user;
  }

  /**
   * Look up name/email for up to 200 userIds in one call (admin-only).
   * POST /v3/admin/users/lookup
   * UserIds with no matching user are simply absent from the response.
   */
  async lookupUsers(userIds: string[]): Promise<BulkUserLookupResponse> {
    const response = await httpService.post<BulkUserLookupResponse>(
      "/v3/admin/users/lookup",
      { userIds },
      { requiresAuth: true, requestScope: "actor" }
    );
    return response.data;
  }

  /**
   * Bulk create/update users and authoritatively sync provider rosters.
   * POST /v3/investors/bulk-update
   * 
   * - Creates users if email doesn't exist
   * - Updates name if provided and user exists
   * - Syncs provider rosters authoritatively for providers in the payload
   */
  async bulkUpdateInvestors(request: BulkUpdateInvestorsRequest): Promise<BulkUpdateInvestorsResponse> {
    const response = await httpService.post<BulkUpdateInvestorsResponse>(
      "/v3/investors/bulk-update",
      request
    );
    return response.data;
  }
}

export const usersService = new UsersService();
export default usersService;
